import React, {
  ForwardedRef,
  forwardRef,
  MouseEventHandler,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useClickAway, useWindowSize } from "react-use";
import styled from "styled-components/macro";
import { Button, ExpandIcon } from "@src/components/elements";
import {
  ButtonProps,
  getActiveBackgroundColor,
  getBackgroundColor,
  getBorder,
  getFillColor,
  getFontColor,
  getFontSize,
  getHoverBackgroundColor,
  getHoveredBorder,
  getHoverFontColor,
  getPadding,
} from "@src/components/elements/ButtonVariants";
import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import { useMouseWheel } from "@src/hooks/useMouseWheel";

const StyledRoot = styled.div<ButtonProps>`
  user-select: none;
  position: relative;
  display: flex;

  .action-button {
    background-color: ${(props) => getBackgroundColor(props)};
    color: ${(props) => getFontColor(props)};
    fill: ${(props) => getFillColor(props)};
    font-size: ${(props) => getFontSize(props)};
    border: ${(props) => getBorder(props)};
    padding: ${(props) => getPadding(props)};

    & svg {
      fill: ${(props) => getFillColor(props)};
    }

    &:hover {
      background-color: ${(props) => getHoverBackgroundColor(props)};
      color: ${(props) => getHoverFontColor(props)};
      border: ${(props) => getHoveredBorder(props)};
      box-shadow: var(--drop-shadow-light);
      text-decoration: none;
    }

    &:active,
    &:focus {
      background-color: ${(props) => getActiveBackgroundColor(props)};
    }
  }
`;

const StyledContent = styled.div<{
  menuPosition: { x: number; y: number };
}>`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 100000;
  opacity: 0;
  background-color: var(--white);
  box-shadow: var(--drop-shadow-dark);
  border: 1px solid var(--border-gray-light);
  transition: opacity 0.2s ease-in-out;
  border-radius: var(--rounded-corner);
  display: flex;
  flex-direction: column;
  width: fit-content;
  transform: ${({ menuPosition }) =>
    `translateX(${menuPosition.x}px) translateY(${menuPosition.y}px)`};
  animation: 0.1s action-menu-slide-down ease-out;

  &.open {
    opacity: 1;
  }
`;

export const StyledMenuContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  .group {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    min-width: 12.5rem;
    padding: 0.75rem;
  }
  a.group:hover {
    text-decoration: none;
    background: var(--background-gray);
    color: var(--primary-black);
  }

  .success-font {
    color: var(--green);
    fill: var(--green);

    svg {
      fill: var(--green);
    }

    &:hover {
      background: var(--background-green);
      color: var(--green);

      svg {
        fill: var(--green);
      }
    }
  }

  .danger-font {
    color: var(--error-color);
    fill: var(--error-color);

    svg {
      fill: var(--error-color);
    }

    &:hover {
      background: var(--background-error-color);
      color: var(--error-color);

      svg {
        fill: var(--error-color);
      }
    }
  }

  .tertiary-font {
    color: var(--primary-black);
    fill: var(--primary-black);

    svg {
      fill: var(--primary-black);
    }

    &:hover {
      background: var(--background-gray);
      color: var(--primary-black);

      svg {
        fill: var(--primary-black);
      }
    }
  }

  .badge {
    color: var(--dark-gray);
    background: var(--border-gray-light);
    border-radius: var(--rounded-corner);
    font-weight: var(--font-weight-medium);
  }

  @media screen and (max-width: 40rem) {
    .badge {
      display: none;
    }
    .mobile-only {
      display: block;
      fill: var(--secondary-black);
    }
  }
`;

export const StyledActionLink = styled(Link)`
  color: var(--dark-gray);
  flex: 1;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: var(--font-size-xsmall);
  svg {
    height: 1rem;
    width: 1rem;
    fill: var(--gray);
    transition: var(--transition);
  }

  &:hover {
    text-decoration: none;
    background: var(--background-gray);
    color: var(--primary-black);
    svg {
      fill: var(--primary-black);
    }
  }
`;

export type ActionMenuTrigger = {
  close: () => void;
  open: () => void;
  toggle: () => void;
};

interface Props extends ButtonProps {
  className?: string;
  actionButton?: ReactNode;
  actionText?: string | ReactNode;
  offset?: { x: number; y: number };
  autocloseOnSelect?: boolean;
  toggleOnClick?: boolean;
  ref?: ForwardedRef<ActionMenuTrigger>;
  clickawayWhiteList?: RefObject<HTMLElement>[] | HTMLElement[];
  clickawayClassnameWhiteList?: string[];
  onOpenChange?: (isOpen: boolean) => void;
  closeOnScroll?: boolean;
}

export const ActionMenu: React.FC<Props> = forwardRef(
  (
    {
      className,
      children,
      actionButton,
      actionText,
      offset,
      variant,
      size,
      active,
      loading,
      disabled,
      autocloseOnSelect = true,
      toggleOnClick = true,
      clickawayWhiteList = [],
      onOpenChange,
      closeOnScroll = true,
      clickawayClassnameWhiteList,
    },
    ref: ForwardedRef<ActionMenuTrigger>,
  ) => {
    const [isOpen, _setIsOpen] = useState(false);
    const rootEl = useRef<HTMLDivElement>(null);
    const contentEl = useRef<HTMLDivElement>(null);
    const { width: windowWidth, height: windowHeight } = useWindowSize();
    const [menuPosition, setMenuPosition] = useState({
      x: -10000,
      y: -10000,
    });

    const setIsOpen = useCallback(
      (open: boolean) => {
        _setIsOpen(open);
        onOpenChange?.(open);
      },
      [onOpenChange],
    );

    useClickAway(contentEl, (e: Event) => {
      if (
        (rootEl.current && rootEl.current.contains(e.target as Node)) ||
        clickawayWhiteList.some((el) =>
          "current" in el
            ? el.current?.contains(e.target as Node)
            : el.contains(e.target as Node),
        ) ||
        clickawayClassnameWhiteList?.some((className) =>
          (e.target as HTMLElement).classList.contains(className),
        )
      ) {
        return;
      }
      setIsOpen(false);
    });

    useImperativeHandle(ref, () => ({
      open() {
        setIsOpen(true);
      },
      close() {
        setIsOpen(false);
      },
      toggle() {
        setIsOpen(!isOpen);
      },
    }));

    useMouseWheel(() => {
      if (closeOnScroll) {
        setIsOpen(false);
        rootEl.current?.blur();
      }
    });

    useEffect(() => {
      const contentElRect = contentEl.current?.getBoundingClientRect();
      const rootElRect = rootEl.current?.getBoundingClientRect();
      if (rootElRect && contentElRect) {
        const isOffscreenBottom =
          rootElRect.top + rootElRect.height + contentElRect.height >
          windowHeight;
        const isOffscreenTop = rootElRect.top - contentElRect.height < 0;
        const isOffscreenRight =
          rootElRect.left + contentElRect.width > windowWidth;

        const offsetX = offset ? offset.x : 0;
        const offsetY = offset ? offset.y : 0;

        if (isOffscreenBottom && isOffscreenTop) {
          // try to position it to the left of the right and center it vertically
          if (isOffscreenRight) {
            // position it to the left of the root element
            setMenuPosition({
              x: rootElRect.x - contentElRect.width - 4 + offsetX,
              y:
                rootElRect.y + rootElRect.height / 2 - contentElRect.height / 2,
            });
          } else {
            // position it to the right of the root element
            setMenuPosition({
              x: rootElRect.x + rootElRect.width + offsetX,
              y:
                rootElRect.y + rootElRect.height / 2 - contentElRect.height / 2,
            });
          }
        } else if (isOffscreenBottom) {
          // position it above the root element
          if (isOffscreenRight) {
            // position it to the left of the root element
            setMenuPosition({
              x: rootElRect.x - contentElRect.width - 4 + offsetX,
              y: rootElRect.y - contentElRect.height - 4 + offsetY,
            });
          } else {
            // position it to the right of the root element
            setMenuPosition({
              x: rootElRect.x + offsetX,
              y: rootElRect.y - contentElRect.height - 4 + offsetY,
            });
          }
        } else {
          // position it below the root element
          if (isOffscreenRight) {
            // position it to the left of the root element
            setMenuPosition({
              x: rootElRect.x - contentElRect.width - 4 + offsetX,
              y: rootElRect.y + rootElRect.height + 4 + offsetY,
            });
          } else {
            // position it to the right of the root element
            setMenuPosition({
              x: rootElRect.x + offsetX,
              y: rootElRect.y + rootElRect.height + 4 + offsetY,
            });
          }
        }
      }
    }, [isOpen, offset, windowWidth, windowHeight]);

    const onContentClick = useCallback<MouseEventHandler<HTMLDivElement>>(
      (event) => {
        event.stopPropagation();
        if (!autocloseOnSelect) {
          return;
        }
        setIsOpen(false);
      },
      [autocloseOnSelect, setIsOpen],
    );

    const contentNode = (
      <StyledContent
        menuPosition={menuPosition}
        className={`${isOpen ? "open" : ""}`}
        ref={contentEl}
        onClick={onContentClick}
      >
        {children}
      </StyledContent>
    );

    const content = createPortal(
      contentNode,
      document.getElementById("app") as Element,
    );

    return (
      <StyledRoot
        ref={rootEl}
        className={`action-menu ${isOpen ? "open" : ""} ${className ?? ""}`}
        variant={variant || "primary"}
        size={size || "medium"}
        active={active || false}
        disabled={loading || disabled}
        role="button"
        title="Actions"
        onScroll={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <div
          onClick={(event) => {
            event.stopPropagation();
            setIsOpen(toggleOnClick ? !isOpen : true);
          }}
          className="full-width"
        >
          {actionButton ? (
            actionButton
          ) : (
            <Button variant={variant || "tertiary"} size={size}>
              <span>{actionText ? actionText : "Actions"}</span>
              <ExpandIcon expanded={isOpen} />
            </Button>
          )}
        </div>
        {isOpen ? content : undefined}
      </StyledRoot>
    );
  },
);

export const StyledFixedHeightActionMenu = styled(ActionMenu)`
  .action-button {
    height: 25px;
    width: 25px;
    padding: 0;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;
